import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import SEO from './SEO';
import { organizationSchema, faqSchema } from '../utils/structuredData';

const About = () => {
  const navigate = useNavigate();

  const faqs = faqSchema([
    {
      question: "What is Infinite Reyn Productions?",
      answer: "Infinite Reyn Productions is a multi-disciplinary creative house built for stories that refuse to stay in one form. We work across Books, Music, and Theatre, allowing each story to choose its medium."
    },
    {
      question: "Who creates the works at Infinite Reyn Productions?",
      answer: "Our works include Books by Evie Montclaire (paranormal romance), Music by Divine Reyn and Wild Reyn (worship music in English and Spanish), and Theatre productions that ask provocative questions."
    },
    {
      question: "What makes Infinite Reyn Productions different?",
      answer: "We don't choose the medium first, we let the story choose how it wants to be told. Our work is shaped by invisible architecture: expertise in psychology, trauma, and the arts that creates emotional precision without ever becoming the subject."
    }
  ]);

  const combinedSchema = {
    "@context": "https://schema.org",
    "@graph": [organizationSchema, faqs]
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <SEO
        title="About | Infinite Reyn Productions"
        description="Infinite Reyn Productions is an independent creative house spanning literature, music, and theatre. Every story finds its medium, and the work is produced at the level it deserves."
        canonical="/about"
        keywords="about Infinite Reyn Productions, Evie Montclaire, Divine Reyn, Wild Reyn, books music theatre, story medium"
        structuredData={combinedSchema}
      />
      {/* Back Button */}
      <button
        onClick={() => navigate('/')}
        className="fixed top-8 left-8 z-50 flex items-center gap-2 text-white/80 hover:text-white transition-colors group"
      >
        <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
        <span className="text-sm tracking-wider">BACK</span>
      </button>

      {/* Navigation Hint */}
      <div className="fixed top-8 right-8 z-50">
        <span className="text-xs tracking-[0.3em] text-gold">ABOUT</span>
      </div>

      <div className="max-w-4xl mx-auto px-8 py-32">
        {/* Opening Statement */}
        <div className="mb-20 text-center">
          <p className="text-4xl md:text-5xl font-light mb-6 tracking-wide">
            INFINITE REYN PRODUCTIONS
          </p>
          <p className="text-xl md:text-2xl font-light text-white/80 mb-12">
            Every story finds its medium.
          </p>
          <p className="text-lg leading-relaxed font-light text-white/90 max-w-3xl mx-auto">
            Infinite Reyn Productions is an independent creative house spanning literature, music, and theatre. We exist because not every story works as a book. Not every truth lands as a song. And some things don't make sense until they're performed live in front of people who didn't know they needed to hear it.
          </p>
          <p className="text-lg leading-relaxed font-light text-white/90 max-w-3xl mx-auto mt-6">
            We don't force a story into a format. We let the story tell us where it belongs, and then we produce it at the level it deserves.
          </p>
        </div>

        {/* Gold Divider */}
        <div className="w-24 h-px bg-gold mx-auto mb-32"></div>

        {/* Three Verticals */}
        <div className="space-y-24 mb-32">
          {/* Books */}
          <div>
            <h3 className="text-xs tracking-[0.3em] text-gold mb-6">BOOKS</h3>
            <h4 className="text-2xl font-light mb-4">Featuring Evie Montclaire</h4>
            <p className="text-lg leading-relaxed font-light text-white/80">
              Gothic, grounded, and sharp paranormal romance with real emotional stakes. Characters you root for even when they're making terrible decisions. The debut series, the Fenmore Pack, launches in 2026.
            </p>
          </div>

          {/* Music */}
          <div>
            <h3 className="text-xs tracking-[0.3em] text-gold mb-6">MUSIC</h3>
            <h4 className="text-2xl font-light mb-4">Featuring Divine Reyn and Wild Reyn</h4>
            <p className="text-lg leading-relaxed font-light text-white/80">
              Bilingual worship and devotion songs that are intimate, beautiful, and spiritual. English and Spanish, sacred and personal.
            </p>
          </div>

          {/* Theatre */}
          <div>
            <h3 className="text-xs tracking-[0.3em] text-gold mb-6">THEATRE</h3>
            <h4 className="text-2xl font-light mb-4">Something is Coming</h4>
            <p className="text-lg leading-relaxed font-light text-white/80">
              An original two-act musical with 34 original songs that bring a classic narrative into a bold, modern theatrical context. Musically ambitious and deeply human, this is a production that makes a timeless story feel like it was written for today.
            </p>
          </div>
        </div>

        {/* Gold Divider */}
        <div className="w-24 h-px bg-gold mx-auto mb-32"></div>

        {/* Closing Statement */}
        <div className="text-center space-y-6 mb-20">
          <p className="text-xl font-light text-white/90">
            Three mediums. One house. The work speaks.
          </p>
        </div>
      </div>
    </div>
  );
};

export default About;
